/**
 * FirstMessageModal - Captures and sends user's first message during onboarding
 *
 * Prompts: "Anything you've always wanted to tell me?"
 * This message is sent IMMEDIATELY so it's already in the chat when they enter.
 *
 * Part of the onboarding flow:
 * WelcomeBackModal -> ProfileSetupModal -> FirstMessageModal -> SubscriptionOnboardingModal
 */

import { useState } from 'react'
import { Modal } from '@/components/ui/Modal'

interface FirstMessageModalProps {
  isOpen: boolean
  onSubmit: (message: string) => void
}

const MAX_MESSAGE_LENGTH = 1000 // Characters
const MIN_MESSAGE_LENGTH = 5 // Minimum to prevent empty/spam submissions

export function FirstMessageModal({ isOpen, onSubmit }: FirstMessageModalProps) {
  const [message, setMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const charactersRemaining = MAX_MESSAGE_LENGTH - message.length
  const isValid = message.trim().length >= MIN_MESSAGE_LENGTH

  const handleSubmit = async () => {
    if (!isValid) {
      setError(`Please enter at least ${MIN_MESSAGE_LENGTH} characters`)
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      // Call the parent callback with the message
      await onSubmit(message.trim())
    } catch (err) {
      console.error('[FirstMessageModal] Error submitting message:', err)
      setError('Failed to save message. Please try again.')
      setIsSubmitting(false)
    }
  }

  // Prevent closing modal without submitting
  const preventClose = () => {
    // Modal cannot be closed - user must submit a message
  }

  return (
    <Modal isOpen={isOpen} onClose={preventClose} showCloseButton={false} maxWidth="max-w-lg">
      <div className="space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="text-5xl mb-4">💬</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            One More Thing...
          </h2>
          <p className="text-gray-600 text-lg">
            Anything you've always wanted to tell me?
          </p>
          <p className="text-pink-600 text-sm mt-2 font-medium">
            I will definitely read this
          </p>
        </div>

        {/* Message Input */}
        <div className="space-y-2">
          <textarea
            value={message}
            onChange={(e) => {
              setMessage(e.target.value.slice(0, MAX_MESSAGE_LENGTH))
              setError(null)
            }}
            placeholder="Type your message here..."
            className="w-full h-32 p-4 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-pink-500 focus:border-transparent text-gray-900 placeholder-gray-400"
            disabled={isSubmitting}
          />

          {/* Character count */}
          <div className="flex justify-between text-sm">
            <span className={`${error ? 'text-red-500' : 'text-gray-500'}`}>
              {error || ' '}
            </span>
            <span className={`${charactersRemaining < 50 ? 'text-orange-500' : 'text-gray-400'}`}>
              {charactersRemaining} characters remaining
            </span>
          </div>
        </div>

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={!isValid || isSubmitting}
          className={`w-full py-3 rounded-lg font-medium shadow-sm transition-colors ${
            isValid && !isSubmitting
              ? 'bg-pink-600 text-white hover:bg-pink-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Saving...
            </span>
          ) : (
            'Send'
          )}
        </button>

        {/* Help text */}
        <p className="text-xs text-gray-400 text-center">
          Your message will be waiting for me when you enter the chat.
        </p>
      </div>
    </Modal>
  )
}
